import type * as MantineTypes from '@mantine/core'
import type { PolymorphicComponentProps } from '@mantine/core'
import { forwardRef } from 'react'
import { ButtonAsync } from './ButtonAsync/ButtonAsync'
import { ChildContent } from './ChildContent/ChildContent'
import { getButtonComponent } from './_utils/getButtonComponent'

export type MantineButtonProps = PolymorphicComponentProps<'button', MantineTypes.ButtonProps>

export type ButtonProps = {
  variant?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'positive'
    | 'negative'
    | 'link'
    | 'link-negative'
    | 'list'
    | 'bare'
  async?: boolean
  asyncOptions?: {
    minimumVisualLoadingMilliseconds?: number
    successMilliseconds?: number
    errorMilliseconds?: number
    loadingContent?: React.ReactNode
    successContent?: React.ReactNode
    errorContent?: React.ReactNode
  }
  gap?: number
  // icons are put into the children for consistency and to deal with some mantine styling issues
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
} & Omit<MantineButtonProps, 'variant'>

const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  if (props.async === true) return <ButtonAsync ref={ref} {...props} />

  // Mantine forwards unknown props to the DOM, so the app-only ones stay out of the spread
  const { variant, leftIcon, rightIcon, gap, async: _async, ...mantineButtonProps } = props
  const ButtonComponent = getButtonComponent(variant)

  return (
    <ButtonComponent
      {...mantineButtonProps}
      ref={ref}
      // Mantine's loader takes the filled variant's white, which vanishes on the white-background variants
      loaderProps={{ type: 'dots', color: 'currentColor', ...props.loaderProps }}
    >
      <ChildContent
        leftIcon={leftIcon}
        content={props.children}
        rightIcon={rightIcon}
        fullWidth={props.fullWidth}
        gap={gap}
      />
    </ButtonComponent>
  )
})

Button.displayName = 'Button'

export { Button }
